import { Button } from "@/components";
import { BsThreeDotsVertical } from "react-icons/bs";
import { memo, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { PositionStatus } from "./position-table-row.utils";
import { PositionRowStatusBadge } from "./PositionRowStatusBadge";

interface DotsMenuButtonProps {
    isOpen: boolean;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export const DotsMenuButton = memo(({ isOpen, onClick }: DotsMenuButtonProps) => (
    <Button variant="ghost" size="sm" aria-expanded={isOpen} onClick={onClick}>
        <BsThreeDotsVertical className="w-4 h-4" />
    </Button>
));

DotsMenuButton.displayName = "DotsMenuButton";

interface PositionTableRowProps {
    durationText: string;
    principal: string;
    rewards: string;
    apy: string;
    isActionDisabled: boolean;
    handleAction: () => void;
    actionButtonText: string;
    isMenuOpen: boolean;
    menuIndex: number | null;
    index: number;
    onMenuToggle: (event: React.MouseEvent<HTMLElement>) => void;
    status?: PositionStatus;
    isSelected: boolean;
    onSelect: () => void;
}

export const PositionTableRow = memo(function PositionTableRow({
    durationText,
    principal,
    rewards,
    apy,
    isActionDisabled,
    handleAction,
    actionButtonText,
    isMenuOpen,
    menuIndex,
    index,
    onMenuToggle,
    status,
    isSelected,
    onSelect,
}: PositionTableRowProps) {
    const isMenuActive = isMenuOpen && menuIndex === index;
    const handleRowAction = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            handleAction();
        },
        [handleAction],
    );

    const handleMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            onMenuToggle(event);
        },
        [onMenuToggle],
    );

    const rowClassName = twMerge(
        "border-t border-portfolio-positions-table transition-colors",
        isSelected ? "bg-portfolio-positions-table-row-selected" : "hover:bg-primary-dim",
    );
    const isLockMoreDisabled =
        isActionDisabled || status === PositionStatus.UNLOCKING || status === PositionStatus.UNLOCKED;
    const actionVariant = actionButtonText === "Lock More" && isLockMoreDisabled ? "secondary" : "primary";

    return (
        <tr className={rowClassName} onClick={onSelect} aria-selected={isSelected}>
            <td className="text-primary p-4">{durationText}</td>
            <td className="text-primary p-4">
                <span className="inline-flex whitespace-nowrap gap-2 items-center">
                    <span>{principal}</span>{" "}
                    <PositionRowStatusBadge status={status ?? PositionStatus.EARNING} unlockAt={null} />
                </span>
            </td>
            <td className="text-secondary p-4">+{rewards} iUSD</td>
            <td className="text-primary p-4">{apy}</td>
            <td className="p-4">
                <div className="inline-flex items-center gap-2">
                    <Button variant={actionVariant} size="sm" onClick={handleRowAction} disabled={isLockMoreDisabled}>
                        {actionButtonText}
                    </Button>
                    <DotsMenuButton isOpen={isMenuActive} onClick={handleMenuClick} />
                </div>
            </td>
        </tr>
    );
});

PositionTableRow.displayName = "PositionTableRow";
